import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import {
  LocalStorageService,
  NotificationService,
  StorageKey,
} from '@blaze-shared/platform-data-access';
import { ButtonComponent, DialogComponent } from '@blaze-shared/shared-ui';

export interface EnableNotificationsDialogInput {
  projectId: string;
}

@Component({
  selector: 'blaze-enable-notifications-dialog',
  imports: [ButtonComponent, DialogComponent, MatDialogModule, MatIconModule],
  templateUrl: './enable-notifications-dialog.component.html',
  styleUrls: ['./enable-notifications-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnableNotificationsDialogComponent {
  private readonly dialogData: EnableNotificationsDialogInput = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<EnableNotificationsDialogComponent>);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly notificationService = inject(NotificationService);

  private readonly projectId: string;

  constructor() {
    this.projectId = this.dialogData.projectId;
  }

  /**
   * Stores timestamp in localStorage of the time the permission dialog was dismissed.
   * It uses this timestamp to ensure the user does not see the dialog again for 7 days.
   */
  protected onDismissPermissionDialog(): void {
    if (!this.projectId) {
      // We can't store information about dialog dismissal. This generally shouldn't happen but
      // it doesn't critically affect the functionality.
      this.dialogRef.close(false);
      return;
    }

    const timestamp = Date.now();
    this.localStorageService.setForProject(
      this.projectId,
      StorageKey.NotificationPermissionDismissal,
      timestamp.toString(),
    );
    this.dialogRef.close(false);
  }

  protected onClose(): void {
    this.dialogRef.close();
  }

  protected async onRequestNotificationPermission(): Promise<void> {
    // The Notification global is absent in some contexts (e.g. iOS Safari outside an
    // installed PWA), where merely referencing it throws a ReferenceError. Bail out
    // early so we skip the dialog and dismissal bookkeeping on unsupported browsers.
    if (typeof Notification === 'undefined') {
      console.warn('Push notifications are not supported in this browser.');
      this.dialogRef.close(false);
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Notification permission not granted:', permission);
        this.notificationService.info(
          'Push notifications permission not granted. You can change it later in your browser settigns',
          true,
        );
        this.dialogRef.close(false);
        return;
      }
      this.dialogRef.close(true);
    } catch (err) {
      console.error('Error requesting notification permission', err);
      this.dialogRef.close(false);
    }
  }
}
